import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  NavLink,
  Link,
  useLocation,
} from "react-router-dom";

import AccessManagementPage from "./pages/AccessManagementPage";
import AddTag from "./pages/AddTag";
import HomePage from "./pages/HomePage";
import GraphPage from "./pages/GraphPage";
import LoginPage from "./pages/LoginPage";
import DynamicPage from "./pages/DynamicPage";
import PageBuilder from "./pages/PageBuilder";
import DesignerPage from "./pages/DesignerPage";
import RuntimePage from "./pages/RuntimePage";

import ProtectedRoute from "./components/ProtectedRoute";
import API, { ensureCsrfToken } from "./services/api";
import { sessionFromProfileStatus } from "./utils/sessionState";

import "./App.css";
import "./BrandSystem.css";
import logo from "./assets/telemindra-lockup.png";

const THEME_STORAGE_KEY = "telemindraIndustrialTheme";
const SCROLL_THRESHOLD = 8;
const TOP_PIN = 16;

const getInitialTheme = () => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === "light" || saved === "dark") {
      return saved;
    }
  } catch (error) {
    console.error("Error reading theme preference:", error);
  }

  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return "light";
};

const rootScroller = () => document.scrollingElement || document.documentElement;

const isViewportTarget = (target) =>
  target === document ||
  target === window ||
  target === document.documentElement ||
  target === document.body ||
  target === rootScroller();

const viewportOffset = () => {
  const element = rootScroller();
  const elementTop = element ? element.scrollTop || 0 : 0;
  const bodyTop = document.body ? document.body.scrollTop || 0 : 0;
  const windowTop = window.scrollY || window.pageYOffset || 0;
  return Math.max(elementTop, bodyTop, windowTop);
};

const canScrollVertically = (element) => {
  if (!(element instanceof Element)) {
    return false;
  }
  if (element.scrollHeight <= element.clientHeight + 1) {
    return false;
  }
  const overflowY = window.getComputedStyle(element).overflowY;
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
};

const scrollSample = (target, header) => {
  if (!(target instanceof Node)) {
    return null;
  }
  if (header && header.contains(target)) {
    return null;
  }
  if (target instanceof Element && target.closest("[data-hmi-drawer]")) {
    return null;
  }
  if (isViewportTarget(target)) {
    return { key: rootScroller(), top: viewportOffset() };
  }
  if (!canScrollVertically(target)) {
    return null;
  }
  return { key: target, top: target.scrollTop || 0 };
};

const AppContent = ({ session, setIsAuthenticated, theme, onToggleTheme, access }) => {
  const serverViewer = !access || ["superadmin", "admin"].includes(access.role);
  const designer = !access || access.can_design;
  const isAuthenticated = session === "authenticated";
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const headerRef = useRef(null);
  const runtimeMenuRef = useRef(null);
  const ioMenuRef = useRef(null);
  const hiddenRef = useRef(false);
  const forceVisibleRef = useRef(false);
  const [runtimePages, setRuntimePages] = useState([]);
  const [ioPages, setIoPages] = useState([]);
  const [runtimeMenuOpen, setRuntimeMenuOpen] = useState(false);
  const [ioMenuOpen, setIoMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navFocused, setNavFocused] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);

  const runtimeActive = location.pathname.startsWith("/runtime");
  const ioActive = location.pathname.startsWith("/p/");
  const forceVisible = runtimeMenuOpen || ioMenuOpen || mobileOpen || navFocused;

  const handleLogout = async () => {
    try {
      await ensureCsrfToken();
      const response = await API.post("/logout/");
      const token = response.data && response.data.csrf_token;
      if (token) {
        localStorage.setItem("token", token);
      } else {
        localStorage.removeItem("token");
      }
    } catch {
      localStorage.removeItem("token");
    }
    setIsAuthenticated(false);
    window.location.href = "/login";
  };

  const fetchRuntimePages = async () => {
    const [designerResult, builderResult] = await Promise.allSettled([
      API.get("/designer/pages/"),
      API.get("/pages/published/"),
    ]);
    const designerPages = designerResult.status === "fulfilled" && Array.isArray(designerResult.value.data)
      ? designerResult.value.data.filter((page) => page.is_published === true).sort((left, right) => (
        Number(left.display_order || 0) - Number(right.display_order || 0)
      )).map((page) => ({
        ...page,
        runtimeKey: `designer-${page.id}`,
        runtimePath: `/runtime/${page.id}`,
      }))
      : [];
    const builderPages = builderResult.status === "fulfilled" && Array.isArray(builderResult.value.data)
      ? builderResult.value.data.map((page) => ({
        ...page,
        runtimeKey: `builder-${page.id}`,
        runtimePath: `/p/${page.id}`,
      }))
      : [];
    setRuntimePages(designerPages);
    setIoPages(builderPages);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchRuntimePages();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const refresh = () => fetchRuntimePages();
    window.addEventListener("aqueris-pages-changed", refresh);
    return () => window.removeEventListener("aqueris-pages-changed", refresh);
    // fetchRuntimePages reads current API state; this listener follows the authenticated session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    setRuntimeMenuOpen(false);
    setIoMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const toggleRuntimeNavigation = () => {
      setRuntimeMenuOpen(false);
      setIoMenuOpen(false);
      setMobileOpen((open) => !open);
    };
    window.addEventListener("aqueris-runtime-nav-toggle", toggleRuntimeNavigation);
    return () => window.removeEventListener("aqueris-runtime-nav-toggle", toggleRuntimeNavigation);
  }, []);

  useEffect(() => {
    forceVisibleRef.current = forceVisible;
    if (forceVisible && hiddenRef.current) {
      hiddenRef.current = false;
      setHeaderHidden(false);
    }
  }, [forceVisible]);

  useEffect(() => {
    const positions = new WeakMap();
    const root = rootScroller();
    if (root) {
      positions.set(root, viewportOffset());
    }

    const seedScroller = (element) => {
      if (!(element instanceof Element) || positions.has(element)) {
        return;
      }
      if (isViewportTarget(element) || (headerRef.current && headerRef.current.contains(element))) {
        return;
      }
      if (!canScrollVertically(element)) {
        return;
      }
      positions.set(element, element.scrollTop || 0);
    };

    const seedTree = (node) => {
      if (!(node instanceof Element)) {
        return;
      }
      seedScroller(node);
      node.querySelectorAll("*").forEach(seedScroller);
    };

    const pendingSeeds = new Set();
    let scanFrame = 0;
    const scheduleSeed = (node) => {
      if (node instanceof Element) {
        pendingSeeds.add(node);
      }
      if (scanFrame) {
        return;
      }
      scanFrame = window.requestAnimationFrame(() => {
        const nodes = Array.from(pendingSeeds);
        pendingSeeds.clear();
        scanFrame = 0;
        nodes.forEach((pending) => seedTree(pending));
      });
    };

    const applyHidden = (next) => {
      const resolved = forceVisibleRef.current ? false : next;
      if (hiddenRef.current === resolved) {
        return;
      }
      hiddenRef.current = resolved;
      setHeaderHidden(resolved);
    };

    const onScroll = (event) => {
      const sample = scrollSample(event.target, headerRef.current);
      if (!sample) {
        return;
      }

      const { key, top } = sample;
      if (top <= TOP_PIN) {
        positions.set(key, top);
        applyHidden(false);
        return;
      }

      if (!positions.has(key)) {
        positions.set(key, top);
        return;
      }
      const previous = positions.get(key);
      positions.set(key, top);
      const delta = top - previous;
      if (Math.abs(delta) < SCROLL_THRESHOLD) {
        return;
      }

      applyHidden(delta > 0);
    };

    if (document.body) {
      seedTree(document.body);
    }
    scheduleSeed(document.body);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            scheduleSeed(node);
          }
        });
      });
    });
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => {
      if (scanFrame) {
        window.cancelAnimationFrame(scanFrame);
      }
      observer.disconnect();
      document.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (!runtimeMenuOpen && !ioMenuOpen && !mobileOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setRuntimeMenuOpen(false);
        setIoMenuOpen(false);
        setMobileOpen(false);
      }
    };

    const onPointerDown = (event) => {
      if (runtimeMenuRef.current && !runtimeMenuRef.current.contains(event.target)) {
        setRuntimeMenuOpen(false);
      }
      if (ioMenuRef.current && !ioMenuRef.current.contains(event.target)) {
        setIoMenuOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [runtimeMenuOpen, ioMenuOpen, mobileOpen]);

  const navClass = ({ isActive }) => (isActive ? "is-active" : undefined);

  return (
    <>
      {!isLoginPage && (
      <>
      <header
        ref={headerRef}
        className={`app-header ${headerHidden && !forceVisible ? "is-hidden" : ""}`}
        data-theme={theme}
        onFocusCapture={() => setNavFocused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setNavFocused(false);
          }
        }}
      >
        <div className="app-header__bar">
          <Link to="/" className="logo-container" aria-label="Telemindra Industrial home">
            <img src={logo} alt="Telemindra" className="company-logo" />
            <span className="product-badge">Industrial</span>
          </Link>

          <button
            type="button"
            className="app-header__menu"
            aria-expanded={mobileOpen}
            aria-controls="app-nav"
            onClick={() => setMobileOpen((open) => !open)}
          >
            <MenuIcon />
            <span>{mobileOpen ? "Close" : "Menu"}</span>
          </button>

          <nav id="app-nav" className={`nav-links ${mobileOpen ? "is-open" : ""}`} aria-label="Primary">
            {runtimeActive && mobileOpen && (
              <button type="button" className="runtime-nav-close" onClick={() => setMobileOpen(false)} aria-label="Close menu" title="Close menu">×</button>
            )}
            <NavLink to="/" end className={navClass}>Overview</NavLink>

            {session === "checking" ? null : isAuthenticated ? (
              <>
                {serverViewer && <NavLink to="/add-tag" className={navClass}>Assets &amp; Tags</NavLink>}
                <NavLink to="/graph" className={navClass}>Trends</NavLink>
                {access?.can_manage_users && <NavLink to="/user-access" className={navClass}>Access</NavLink>}
                {serverViewer && <NavLink to="/page-builder" className={navClass}>Pages</NavLink>}
                {designer && <NavLink to="/designer" className={navClass}>Designer</NavLink>}

                <div className={`runtime-menu ${runtimeActive ? "is-active" : ""}`} ref={runtimeMenuRef}>
                  <button
                    type="button"
                    className="runtime-menu-button"
                    aria-expanded={runtimeMenuOpen}
                    aria-haspopup="true"
                    aria-controls="runtime-page-menu"
                    onClick={() => {
                      setRuntimeMenuOpen((prev) => !prev);
                      setIoMenuOpen(false);
                    }}
                  >
                    Runtime
                    <span aria-hidden="true">{runtimeMenuOpen ? "▴" : "▾"}</span>
                  </button>

                  {runtimeMenuOpen && (
                    <div id="runtime-page-menu" className="runtime-dropdown" role="menu">
                      {runtimePages.length === 0 ? (
                        <div className="runtime-empty">No published pages</div>
                      ) : (
                        runtimePages.map((page) => (
                          <Link
                            key={page.runtimeKey || page.id}
                            to={page.runtimePath || `/runtime/${page.id}`}
                            className="runtime-dropdown-item"
                            role="menuitem"
                            onClick={() => setRuntimeMenuOpen(false)}
                          >
                            {page.title}
                          </Link>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div className={`runtime-menu ${ioActive ? "is-active" : ""}`} ref={ioMenuRef}>
                  <button
                    type="button"
                    className="runtime-menu-button"
                    aria-expanded={ioMenuOpen}
                    aria-haspopup="true"
                    aria-controls="io-page-menu"
                    onClick={() => {
                      setIoMenuOpen((prev) => !prev);
                      setRuntimeMenuOpen(false);
                    }}
                  >
                    I/O
                    <span aria-hidden="true">{ioMenuOpen ? "▴" : "▾"}</span>
                  </button>

                  {ioMenuOpen && (
                    <div id="io-page-menu" className="runtime-dropdown" role="menu">
                      {ioPages.length === 0 ? (
                        <div className="runtime-empty">No published I/O pages</div>
                      ) : (
                        ioPages.map((page) => (
                          <Link
                            key={page.runtimeKey || page.id}
                            to={page.runtimePath || `/p/${page.id}`}
                            className="runtime-dropdown-item"
                            role="menuitem"
                            onClick={() => setIoMenuOpen(false)}
                          >
                            {page.title}
                          </Link>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="app-header__theme"
                  onClick={onToggleTheme}
                  aria-pressed={theme === "dark"}
                  aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {theme === "dark" ? "Dark" : "Light"}
                </button>

                <button type="button" onClick={handleLogout} className="logout-button">
                  Sign out
                </button>
              </>
            ) : (
              <NavLink to="/login" className={navClass}>Login</NavLink>
            )}
          </nav>
        </div>
      </header>
      <div className="app-header-spacer" aria-hidden="true" />
      </>
      )}

      <Routes>
        <Route
          path="/login"
          element={<LoginPage setIsAuthenticated={setIsAuthenticated} />}
        />

        <Route path="/" element={<ProtectedRoute session={session}><HomePage theme={theme} onToggleTheme={onToggleTheme} access={access} /></ProtectedRoute>} />
        <Route path="/add-server" element={<ProtectedRoute session={session} allowed={serverViewer}><Navigate to="/add-tag?tab=server" replace /></ProtectedRoute>} />
        <Route path="/add-tag" element={<ProtectedRoute session={session} allowed={serverViewer}><AddTag access={access} /></ProtectedRoute>} />
        <Route path="/graph" element={<ProtectedRoute session={session}><GraphPage /></ProtectedRoute>} />
        <Route path="/page-builder" element={<ProtectedRoute session={session} allowed={serverViewer}><PageBuilder theme={theme} /></ProtectedRoute>} />
        <Route path="/p/:id" element={<ProtectedRoute session={session}><DynamicPage theme={theme} access={access} /></ProtectedRoute>} />
        <Route path="/designer" element={<ProtectedRoute session={session} allowed={designer}><DesignerPage /></ProtectedRoute>} />

        <Route path="/user-access" element={<ProtectedRoute session={session}><AccessManagementPage access={access} theme={theme} /></ProtectedRoute>} />
        <Route path="/runtime/:id" element={<RuntimePage />} />
      </Routes>
    </>
  );
};

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M4 7.25h16a.9.9 0 1 0 0-1.8H4a.9.9 0 0 0 0 1.8Zm0 5.65h16a.9.9 0 1 0 0-1.8H4a.9.9 0 0 0 0 1.8Zm0 5.65h16a.9.9 0 1 0 0-1.8H4a.9.9 0 0 0 0 1.8Z" />
  </svg>
);

const App = () => {
  const [session, setSession] = useState("checking");
  const [access, setAccess] = useState(null);
  const [profileEpoch, setProfileEpoch] = useState(0);
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    let cancelled = false;
    API.get("/profile/")
      .then((response) => {
        if (!cancelled) {
          setAccess(response.data?.access || null);
          setSession(sessionFromProfileStatus(response.status));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          const status = error && error.response ? error.response.status : undefined;
          setAccess(null);
          setSession(sessionFromProfileStatus(status));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [profileEpoch]);

  const setIsAuthenticated = (authenticated) => {
    setAccess(null);
    if (authenticated) {
      setSession("checking");
      setProfileEpoch((value) => value + 1);
    } else {
      setSession("anonymous");
    }
  };

  const onToggleTheme = () => {
    setTheme((current) => {
      const next = current === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch (error) {
        console.error("Error saving theme preference:", error);
      }
      return next;
    });
  };

  return (
    <Router>
      <AppContent
        session={session}
        access={access}
        setIsAuthenticated={setIsAuthenticated}
        theme={theme}
        onToggleTheme={onToggleTheme}
      />
    </Router>
  );
};

export default App;
